import type {
  Agent,
  AgentDeployment,
  AgentVersion,
  AgentVersionSummary,
  CreateAgentInput,
  Execution,
} from './types';

export class RegistryError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly reference?: string,
  ) {
    super(message);
    this.name = 'RegistryError';
  }
}

export interface RegistryTransport {
  (method: string, path: string, body?: unknown): Promise<unknown>;
}

export function createRegistryTransport(
  fetcher: typeof fetch,
  baseUrl: string,
  token: () => string | undefined,
): RegistryTransport {
  return async (method, path, body) => {
    const accessToken = token();
    if (!accessToken) {
      throw new RegistryError('Sign in is required before Agent Registry calls.', 401);
    }
    const headers: Record<string, string> = {
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    };
    if (body !== undefined) {
      headers['Content-Type'] = 'application/json';
    }
    const response = await fetcher(`${baseUrl}${path}`, {
      method,
      headers,
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const text = await response.text();
    const payload = text ? safeJson(text) : undefined;
    if (!response.ok) {
      throw new RegistryError(registryMessage(response.status, payload), response.status, referenceOf(payload));
    }
    return payload;
  };
}

export class AgentRegistryClient {
  constructor(private readonly transport: RegistryTransport) {}

  listAgents() {
    return this.transport('GET', '/v1/agents') as Promise<Agent[]>;
  }

  getAgent(agentId: string) {
    return this.transport('GET', `/v1/agents/${agentId}`) as Promise<Agent>;
  }

  createAgent(input: Pick<CreateAgentInput, 'agentKey' | 'name' | 'description' | 'owner'>) {
    return this.transport('POST', '/v1/agents', {
      agentKey: input.agentKey,
      name: input.name,
      description: input.description,
      owner: input.owner,
    }) as Promise<Agent>;
  }

  listVersions(agentId: string) {
    return this.transport('GET', `/v1/agents/${agentId}/versions`) as Promise<AgentVersionSummary[]>;
  }

  getVersion(agentId: string, versionId: string) {
    return this.transport('GET', `/v1/agents/${agentId}/versions/${versionId}`) as Promise<AgentVersion>;
  }

  createVersion(agentId: string, input: CreateAgentInput) {
    return this.transport('POST', `/v1/agents/${agentId}/versions`, versionBody(input)) as Promise<AgentVersion>;
  }

  updateDraft(agentId: string, versionId: string, input: Partial<CreateAgentInput>) {
    return this.transport('PATCH', `/v1/agents/${agentId}/versions/${versionId}`, versionBody(input)) as Promise<AgentVersion>;
  }

  validate(agentId: string, versionId: string) {
    return this.transport('POST', `/v1/agents/${agentId}/versions/${versionId}/validate`) as Promise<AgentVersion>;
  }

  publish(agentId: string, versionId: string) {
    return this.transport('POST', `/v1/agents/${agentId}/versions/${versionId}/publish`) as Promise<AgentVersion>;
  }

  activateDev(agentId: string, versionId: string, reason: string) {
    return this.activate(agentId, 'DEV', versionId, reason);
  }

  activate(agentId: string, environment: string, versionId: string, reason: string) {
    if (environment === 'PROD') {
      throw new RegistryError('PROD activation is not available from Studio in this increment.', 403);
    }
    return this.transport('PUT', `/v1/agents/${agentId}/deployments/${environment}`, {
      agentVersionId: versionId,
      reason,
    }) as Promise<AgentDeployment>;
  }

  getDeployment(agentId: string, environment: 'DEV' | 'UAT' | 'PROD') {
    return this.transport('GET', `/v1/agents/${agentId}/deployments/${environment}`) as Promise<AgentDeployment>;
  }

  execute(agentKey: string, environment: string, input: Record<string, unknown>, traceId?: string) {
    if (environment === 'PROD') {
      throw new RegistryError('Test runs are not sent to PROD from Studio.', 403);
    }
    return this.transport('POST', '/v1/executions', {
      agentKey,
      environment,
      input,
      traceId,
    }) as Promise<Execution>;
  }
}

function versionBody(input: Partial<CreateAgentInput>) {
  return {
    versionLabel: input.versionLabel,
    role: input.role,
    goal: input.goal,
    instructions: input.instructions,
    modelPolicy: input.modelPolicy,
    memoryPolicy: input.memoryPolicy,
    permissionPolicy: input.permissionPolicy,
    approvalPolicy: input.approvalPolicy,
    outputSchema: input.outputSchema,
    evaluationPolicy: input.evaluationPolicy,
    metadata: input.capability ? { capability: input.capability, templateId: input.templateId } : undefined,
    toolBindings: input.toolBindings,
    knowledgeBindings: input.knowledgeBindings,
  };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return { message: text.slice(0, 180) };
  }
}

function referenceOf(payload: unknown) {
  if (!payload || typeof payload !== 'object') {
    return undefined;
  }
  const record = payload as Record<string, unknown>;
  const reference = record.correlationId ?? record.reference ?? record.traceId;
  return typeof reference === 'string' ? reference : undefined;
}

export function registryMessage(status: number, payload: unknown) {
  const record = payload && typeof payload === 'object' ? (payload as Record<string, unknown>) : {};
  let detail = '';
  if (typeof record.message === 'string') {
    detail = record.message;
  } else if (typeof record.error === 'string') {
    detail = record.error;
  }
  if (status === 401) return 'Sign in is required. The Agent Registry did not accept this session.';
  if (status === 403) return detail || 'You are not allowed to perform this agent action.';
  if (status === 404) return 'That agent was not found in this tenant.';
  if (status === 409) return detail || 'The version lifecycle does not allow this action.';
  if (status >= 500) return 'The Agent Registry is unavailable. Retry, and quote the reference if it is shown.';
  return detail || 'The Agent Registry rejected the request.';
}
